import {
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  ControlContainer,
  FormControl,
  FormGroup,
  FormGroupDirective,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { PortingWindow } from '@app/sales/modules/subscription/services/porting.service';
import { CustomerProductSelectors } from '@app/sales/states/customer-product/customer-product.selectors';
import { ShoppingCartSelectors } from '@app/sales/states/shopping-cart/shopping-cart.selectors';
import { IconNames } from '@app/shared/enums/icon-names';
import { select } from '@ngxs/store';
import { dateFns } from '@core/date-utils';
import { NotificationService } from '@app/core/services/notification.service';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { IceSelectComponent } from '@app/shared/components/molecules/ice-select/ice-select.component';
import { IceDatePickerComponent } from '@app/shared/components/molecules/ice-date-picker/ice-date-picker.component';
import { DisableControlDirective } from '@app/shared/directives/disable-control.directive';
import { IceSelectBoxComponent } from '@app/shared/components/molecules/ice-select-box/ice-select-box.component';
import Moment from 'moment';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';

@Component({
  selector: 'app-activation-date',
  templateUrl: './activation-date.component.html',
  styleUrl: './activation-date.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    IceDatePickerComponent,
    FormsModule,
    ReactiveFormsModule,
    IceSelectBoxComponent,
    IceSelectComponent,
    IceAlertComponent,
    DisableControlDirective,
  ],
})
export class ActivationDateComponent implements OnInit, OnChanges, OnDestroy {
  formGroupDirective = inject(FormGroupDirective);
  private notificationService = inject(NotificationService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  IconNames = IconNames;

  allowedPortingDates = select(CustomerProductSelectors.allowedPortingDates);
  hasShoppingCartLogisticsVoiceProduct = select(ShoppingCartSelectors.hasShoppingCartLogisticsVoiceProduct);

  @Input() isPorting: boolean;
  @Input() orderSim: boolean;
  @Input() isPhysicalSim: boolean;
  @Input() initialIsEarliestPortingDate: boolean = true;
  @Output() isEarliestPortingDate = new EventEmitter<boolean>();

  form: FormGroup;
  isEarliestPortingTimeControl: FormControl;
  hasAutomaticallyDelayedActivationDate = false;
  private isFirstPortingTimeCheck = true;
  datePipe = new DatePipe('nb');
  portingTimes: string[] = [];
  isPortingTimeSelectionDisabled = false;

  subscriptions$: Subscription[] = [];

  constructor() {
    effect(() => {
      if (!this.allowedPortingDates() || !this.activationDateControl) {
        return;
      }
      const isPortingDateSet = this.activationDateControl.value;
      const shouldUseEarliestDate = !this.isEarliestPortingTimeControl || this.isEarliestPortingTimeControl.value;

      if (!isPortingDateSet && shouldUseEarliestDate) {
        this.updatePortingDateToEarliestPossible();
      }
      this.setPortingTimes();
      this.setInitialValues();

      this.changeDetectorRef.detectChanges();
    });
    effect(() => {
      if (!this.hasShoppingCartLogisticsVoiceProduct()) {
        this.checkPortingTimes();
      }
    });
  }

  ngOnInit() {
    this.form ||= this.formGroupDirective.form;
    this.isEarliestPortingTimeControl = new FormControl(this.initialIsEarliestPortingDate);

    this.subscriptions$.push(
      this.activationDateControl.valueChanges.subscribe(value => {
        // Remove the delay flag if the user changes the date manually
        this.hasAutomaticallyDelayedActivationDate &&= this.activationDateControl.pristine;

        const isValidDate = dateFns.isValidMomentDate(value, ['DD.MM.YYYY', 'DDMMYY']);
        if (isValidDate) {
          this.setInitialValues();
        }
      }),
      this.portingTimeControl.valueChanges.subscribe(() => {
        this.isEarliestPortingDate.emit(this.isEarliestPortingTime);
      }),
      this.isEarliestPortingTimeControl.valueChanges.subscribe(value => {
        if (value) this.updatePortingDateToEarliestPossible();
      })
    );

    const eidControl = this.form?.controls.eid as FormControl;
    if (eidControl) {
      this.handleEidChange(eidControl.value);

      this.subscriptions$.push(
        eidControl.valueChanges.subscribe(value => {
          this.handleEidChange(value);
        })
      );
    }
  }

  setInitialValues() {
    this.isEarliestPortingDate.emit(this.isEarliestPortingTime);
    this.updateSelectedPortingTime();
  }

  //Using ngOnChanges instead of listening to the formControls directly,
  //because the said formControls are dependant on and managed by the parent component
  ngOnChanges(changes: SimpleChanges): void {
    this.form ||= this.formGroupDirective.form;

    if (
      !changes.isPorting?.firstChange ||
      !changes.orderSim?.firstChange ||
      (changes.isPhysicalSim && !changes.isPhysicalSim?.firstChange)
    ) {
      this.checkPortingTimes();
    }

    if (!!changes.isPorting) {
      changes.isPorting.currentValue ? this.portingTimeControl.enable() : this.portingTimeControl.disable();
      this.changeDetectorRef.markForCheck();
    }
  }

  updateSelectedPortingTime() {
    if (this.isPorting) {
      const updatedActivationDateTime = dateFns.getDateTimeWithSetTime(
        this.activationDateControl.value,
        this.portingTimeControl.value
      );

      const portingWindows = this.mustUseLogisticsWindows
        ? this.allowedPortingDatesWithLogistics
        : this.allowedPortingDatesWithoutLogistics;
      const correspondingPortingWindow = portingWindows?.find(
        d => new Date(d.date).toDateString() === new Date(updatedActivationDateTime).toDateString()
      );

      if (!correspondingPortingWindow) {
        this.notificationService.info(
          `Overføringstidsdato er ikke gyldig, første mulige er ${this.datePipe.transform(
            this.earliestPortingDate,
            'd.MM.yyyy'
          )}`
        );

        return;
      }

      const isPortingTimeAllowed =
        updatedActivationDateTime.getTime() >= new Date(correspondingPortingWindow?.date).getTime() &&
        this.isPortingHoursWithinHourlyWindow(updatedActivationDateTime, correspondingPortingWindow);

      if (!isPortingTimeAllowed) {
        const newPortingTime = dateFns.getTimeStringFromDateTime(correspondingPortingWindow?.date);

        this.form.controls.portingTime.patchValue(newPortingTime);
        this.notificationService.info(
          `Overføringstidspunkt endret til kl. ${this.datePipe.transform(
            correspondingPortingWindow?.date,
            'HH:mm'
          )} (første mulige for ${this.datePipe.transform(correspondingPortingWindow?.date, 'd.MMMM')})`
        );
      }

      this.portingTimes = this.calcPortingTimes(correspondingPortingWindow);
    }
  }

  get allowedPortingDatesWithLogistics() {
    return this.allowedPortingDates()?.withLogistics;
  }

  get allowedPortingDatesWithoutLogistics() {
    return this.allowedPortingDates()?.withoutLogistics;
  }

  get isEarliestPortingTime(): boolean {
    const activationDateValue = new Date(this.activationDateControl?.value).toDateString();
    const minDateValue = this.minDate?.toDateString();
    const isEarliestDay = activationDateValue === minDateValue;

    const portingTime = this.portingTimeControl?.value;
    const minDateTime = dateFns.getTimeStringFromDateTime(this.minDate);
    const isEarliestTime = portingTime === minDateTime || this.portingTimeControl?.disabled;

    return isEarliestDay && isEarliestTime;
  }

  setPortingTimes() {
    if (!this.allowedPortingDates()) return;

    this.portingTimes = this.calcPortingTimes(
      this.mustUseLogisticsWindows ? this.allowedPortingDatesWithLogistics[0] : this.allowedPortingDatesWithoutLogistics[0]
    );

    if (this.portingTimeControl.enabled && !this.portingTimes.includes(this.portingTimeControl.value)) {
      this.portingTimeControl.reset(this.portingTimes[0]);
    }
  }

  get activationDateControl(): FormControl {
    return this.form?.controls.activationDateTime as FormControl;
  }

  get portingTimeControl(): FormControl {
    return this.form?.controls.portingTime as FormControl;
  }

  private handleEidChange(eidValue: string | null): void {
    const hasEid = !!eidValue;

    if (hasEid) {
      this.isPortingTimeSelectionDisabled = true;

      if (!this.isEarliestPortingTimeControl.value) {
        this.isEarliestPortingTimeControl.setValue(true);
      }
    } else {
      this.isPortingTimeSelectionDisabled = false;
    }
    this.changeDetectorRef.detectChanges();
  }

  private checkPortingTimes(): void {
    if (!this.isEarliestPortingTimeControl) {
      return;
    }

    const hasCustomPortingTime = !this.isEarliestPortingTimeControl.value;
    const isDateTimeControlDirty = !this.activationDateControl.pristine || !this.portingTimeControl.pristine;
    const activationDate = new Date(this.activationDateControl.value);
    const isActivationDateWithinAllowedRange = activationDate <= this.maxDate && activationDate >= this.minDate;
    const isPortingTimeWithinAllowedRange =
      this.portingTimeControl.disabled || this.portingTimes.includes(this.portingTimeControl.value);

    const shouldKeepUserSelectedDate =
      hasCustomPortingTime &&
      isActivationDateWithinAllowedRange &&
      isPortingTimeWithinAllowedRange &&
      (isDateTimeControlDirty || this.isFirstPortingTimeCheck);

    if (!shouldKeepUserSelectedDate) {
      this.updatePortingDateToEarliestPossible();
    }

    this.isFirstPortingTimeCheck = false;
    this.setPortingTimes();
  }

  private updatePortingDateToEarliestPossible() {
    let date = this.minDate;

    // If new number and orderSim is chosen - we will allow todays date as the minimum date,
    // but we want to set it to the first logistics date by default because of the delivery time
    if (!this.isPorting && this.orderSim && this.allowedPortingDatesWithLogistics?.length > 0) {
      date = new Date(this.allowedPortingDatesWithLogistics[0].date);
    }

    if (!!this.activationDateControl.value && date > new Date(this.activationDateControl.value)) {
      this.hasAutomaticallyDelayedActivationDate = true;
    } else {
      this.hasAutomaticallyDelayedActivationDate = false;
    }

    this.activationDateControl.reset(Moment(date, 'DDMMYY', true));
    this.portingTimeControl.reset(dateFns.getTimeStringFromDateTime(date));
  }

  private calcPortingTimes(portingDateWindow: PortingWindow): string[] {
    const portingTimes = [];
    const { allowedPortingHoursStart, allowedPortingHoursEnd, date } = portingDateWindow;
    const portingDateWindowDateMinutes = new Date(date)?.getMinutes();

    for (let i = allowedPortingHoursStart; i <= allowedPortingHoursEnd; i++) {
      if (i !== allowedPortingHoursStart || portingDateWindowDateMinutes === 0) {
        portingTimes.push(`${i < 10 ? '0' + i : i}:00`);
      }
      if (i < allowedPortingHoursEnd) {
        portingTimes.push(`${i < 10 ? '0' + i : i}:30`);
      }
    }

    return portingTimes;
  }

  private isPortingHoursWithinHourlyWindow(updatedActivationDateTime: Date, portingWindow: PortingWindow): boolean {
    if (updatedActivationDateTime.getHours() < portingWindow.allowedPortingHoursStart) {
      return false;
    }

    if (updatedActivationDateTime.getHours() > portingWindow.allowedPortingHoursEnd) {
      return false;
    }

    if (
      updatedActivationDateTime.getHours() === portingWindow.allowedPortingHoursEnd &&
      updatedActivationDateTime.getMinutes() > 0
    ) {
      return false;
    }

    return true;
  }

  get mustUsePortingTimes(): boolean {
    return this.isPorting || this.isDelayedESim;
  }

  get mustUseLogisticsWindows(): boolean {
    if (this.mustUsePortingTimes) {
      return this.orderSim || this.isDelayedESim;
    }

    return false;
  }

  get isDelayedESim(): boolean {
    return !this.isPhysicalSim && this.hasShoppingCartLogisticsVoiceProduct();
  }

  get allowedPickerDates(): Date[] {
    if (this.mustUsePortingTimes) {
      return this.mustUseLogisticsWindows
        ? this.allowedPortingDatesWithLogistics.map(portingWindow => new Date(portingWindow.date))
        : this.allowedPortingDatesWithoutLogistics.map(portingWindow => new Date(portingWindow.date));
    }
    return [];
  }

  get minDate(): Date {
    if (this.mustUsePortingTimes) {
      return this.earliestPortingDate;
    }

    return dateFns.getDateTimeWithSetTime(new Date().toDateString(), '06:00');
  }

  get maxDate(): Date {
    var date = new Date();
    date.setMonth(date.getMonth() + 2);
    return date;
  }

  get earliestPortingDate(): Date {
    const withLogisticsDate = this.allowedPortingDatesWithLogistics?.[0]?.date ?? null;
    const withoutLogisticsDate = this.allowedPortingDatesWithoutLogistics?.[0]?.date ?? null;
    const isOrderOrDelayedSim = this.orderSim || this.isDelayedESim;

    return new Date(isOrderOrDelayedSim ? withLogisticsDate : withoutLogisticsDate);
  }

  get estimatedTransferDateText(): string {
    const dateString = Moment.utc(this.earliestPortingDate, 'DD.MM.YYYY').format('DD.MM.YYYY');
    const timeString = dateFns.getTimeStringFromDateTime(this.earliestPortingDate);
    return `${dateString} kl. ${timeString}`;
  }

  ngOnDestroy(): void {
    this.subscriptions$.forEach(sub => sub.unsubscribe());
  }
}
